import { PidNode } from './PidNode'
import { cx, TagLabel, type PidBaseProps } from './shared'

export interface PumpProps extends PidBaseProps {
  running?: boolean
  /** 0–100 speed / load. */
  speed?: number
  /**
   * Trapezoid-end angle. Same convention as Pipe: 0 = right, 90 = down.
   * Default 90. Ends stay opposite — `output` sets orientation.
   */
  input?: number
  /**
   * Rectangle-end angle (discharge neck). Default 270 (up), matching the
   * usual upright pump symbol.
   */
  output?: number
  /** Show the on/off status LED on the glyph. Default false. */
  indicator?: boolean
  layout?: 'port' | 'stack'
}

const SIZE = 56
const CX = SIZE / 2
const CY = SIZE / 2
const R = 16
const NECK_H = 12
const NECK_W = R * 0.52
const TRAP_H = 10
/** Canonical drawing has the rectangle pointing up (270°). */
const CANON_OUTPUT = 270
const FILL = '#fff'
const STROKE = 'var(--foreground, #0a0a0a)'

export function Pump({
  running = false,
  speed = 0,
  input: _input = 90,
  output = 270,
  indicator = false,
  layout = 'port',
  tag,
  className,
  style,
  title
}: PumpProps) {
  const shown = running ? Math.round(speed) : 0
  // Silhouette is fixed opposite (rect ↔ trap); `output` aims the rectangle.
  const rotate = output - CANON_OUTPUT
  const trapTopY = CY + R - 3
  const trapBotY = CY + R + TRAP_H
  // Half-width of the circle at the contact y — top of trap must be narrower.
  const arcHalf = Math.sqrt(R * R - (trapTopY - CY) ** 2)
  const trapTop = arcHalf * 0.62
  const trapBot = arcHalf * 0.88

  const glyph = (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
      <g transform={`rotate(${rotate} ${CX} ${CY})`}>
        {/* Neck + base first; casing drawn last covers the shared edges. */}
        <rect
          x={CX - NECK_W / 2}
          y={CY - R - NECK_H}
          width={NECK_W}
          height={NECK_H + 6}
          fill={FILL}
          stroke={STROKE}
          strokeWidth={1.5}
        />
        <path
          d={[
            `M${CX - trapTop} ${trapTopY}`,
            `L${CX + trapTop} ${trapTopY}`,
            `L${CX + trapBot} ${trapBotY}`,
            `L${CX - trapBot} ${trapBotY}`,
            'Z'
          ].join(' ')}
          fill={FILL}
          stroke={STROKE}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        <circle cx={CX} cy={CY} r={R} fill={FILL} stroke={STROKE} strokeWidth={1.5} />
      </g>
      {indicator && (
        <circle
          className={running ? 'pid-status-on' : 'pid-status-off'}
          cx={CX + 18}
          cy={CY - 18}
          r={3.5}
        />
      )}
    </svg>
  )

  return (
    <div
      style={style}
      title={title ?? tag}
      className={cx(running && 'pid-node-running', className)}
    >
      <PidNode glyph={glyph} glyphHeight={SIZE} mode={layout}>
        <div className="pid-status-row">
          <span className={cx('pid-status-text', running ? 'is-on' : 'is-off')}>
            {running ? 'On' : 'Off'}
          </span>
          <div className="pid-readout">
            <span className="pid-readout-value">{shown}</span>
            <span className="pid-readout-unit">%</span>
          </div>
        </div>
        <TagLabel>{tag}</TagLabel>
      </PidNode>
    </div>
  )
}
